import { 
  Utensils, Building, Heart, Calendar, Award, Users, Shield, Zap, Target, 
  Settings, Database, Cpu, TrendingUp, BarChart3, Globe, Activity, Lock, 
  Wifi, Search, RefreshCw, Clock, Package, Coffee, MapPin, Star
} from 'lucide-react';
import { ProductConfigs } from './types';

export const serviceHospitalityConfigs: ProductConfigs = {
  'restaurant-management-system': {
    keyBenefits: [
      {
        icon: Utensils,
        title: 'Complete POS Integration',
        description: 'Advanced point-of-sale system with menu management, order tracking, and payment processing across all channels',
        metrics: '50% order efficiency increase',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Revenue Optimisation Analytics',
        description: 'AI-powered sales analytics with menu engineering, pricing optimisation, and profit margin analysis',
        metrics: '35% revenue growth',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Target,
        title: 'Smart Inventory Management',
        description: 'Intelligent inventory control with automated ordering, waste reduction, and supplier management systems',
        metrics: '40% inventory optimisation',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Users,
        title: 'Customer Loyalty Excellence',
        description: 'Advanced customer analytics with loyalty programmes, personalised offers, and retention strategies',
        metrics: '65% customer retention',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Clock,
        title: 'Staff Scheduling Intelligence',
        description: 'Optimised staff scheduling with labour cost control, shift management, and performance tracking',
        metrics: '30% labour cost reduction',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Financial Reporting Suite',
        description: 'Comprehensive financial analytics with real-time reporting, cost analysis, and profit optimisation',
        metrics: 'Complete financial visibility',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Kitchen Display Management',
        description: 'Advanced kitchen operations with order workflow, cooking time optimisation, and quality control systems',
        metrics: 'Kitchen efficiency mastery',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Cpu,
        title: 'AI Menu Engineering',
        description: 'Machine learning-powered menu optimisation with demand prediction and profitability analysis',
        metrics: '95% demand forecast accuracy',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Management',
        description: 'Centralised management for multiple restaurant locations with consolidated reporting and standardisation',
        metrics: 'Scalable operations platform',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Package,
        title: 'Supply Chain Integration',
        description: 'Advanced supplier management with automated procurement, quality tracking, and cost optimisation',
        metrics: 'Supply chain excellence',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Heart,
        title: 'Customer Experience Platform',
        description: 'Integrated customer experience management with feedback collection, sentiment analysis, and service improvement',
        metrics: '90% customer satisfaction',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Settings,
        title: 'Table Management Automation',
        description: 'Intelligent table allocation with reservation management, waitlist optimisation, and service time tracking',
        metrics: '85% table turnover efficiency',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated audit trails and regulatory reporting',
        metrics: 'Enterprise compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat intelligence, anomaly detection, and incident response automation',
        metrics: '24/7 threat protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime with auto-scaling security systems and distributed denial-of-service protection',
        metrics: 'Maximum availability',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Wifi,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layered security with intelligent threat filtering and adaptive defence mechanisms',
        metrics: 'Proactive threat defence',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication',
        metrics: 'Zero-trust security',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Search,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with correlation analysis, threat hunting, and automated incident response',
        metrics: 'Intelligent security ops',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Advanced data protection with encrypted backups, point-in-time recovery, and data loss prevention',
        metrics: 'Complete data protection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and lateral movement prevention',
        metrics: 'Network security isolation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and security posture management',
        metrics: 'Continuous security validation',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'hotel-management-system': {
    keyBenefits: [
      {
        icon: Building,
        title: 'Integrated Reservation Management',
        description: 'Complete booking system with channel management, rate optimisation, and guest preference tracking',
        metrics: '45% booking efficiency increase',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Revenue Management Excellence',
        description: 'Dynamic pricing strategies with demand forecasting, competitor analysis, and yield optimisation',
        metrics: '40% revenue per room increase',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Users,
        title: 'Guest Experience Intelligence',
        description: 'Personalised guest services with preference tracking, satisfaction monitoring, and loyalty management',
        metrics: '70% guest satisfaction increase',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Clock,
        title: 'Housekeeping Automation',
        description: 'Intelligent housekeeping operations with task scheduling, quality control, and maintenance tracking',
        metrics: '50% housekeeping efficiency',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Target,
        title: 'Property Operations Optimisation',
        description: 'Streamlined property management with maintenance scheduling, asset tracking, and cost control',
        metrics: '35% operational cost reduction',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Financial Performance Analytics',
        description: 'Comprehensive financial reporting with profitability analysis, budget management, and cost optimisation',
        metrics: 'Complete financial intelligence',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Guest Profile Management',
        description: 'Advanced guest database with preference tracking, history analysis, and personalisation engine',
        metrics: 'Complete guest intelligence',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Cpu,
        title: 'AI-Powered Demand Forecasting',
        description: 'Machine learning demand prediction with seasonal analysis, event impact, and pricing optimisation',
        metrics: '96% occupancy forecast accuracy',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Globe,
        title: 'Channel Management Integration',
        description: 'Seamless integration with OTA platforms, direct booking optimisation, and rate parity management',
        metrics: 'Omnichannel booking excellence',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Heart,
        title: 'Concierge Service Platform',
        description: 'Advanced guest services with request management, local recommendations, and experience personalisation',
        metrics: '95% service satisfaction',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Settings,
        title: 'Facility Management System',
        description: 'Comprehensive facility operations with preventive maintenance, energy management, and compliance tracking',
        metrics: 'Facility excellence platform',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Package,
        title: 'Event & Conference Management',
        description: 'Integrated event planning with space management, catering coordination, and revenue optimisation',
        metrics: 'Event management mastery',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated audit trails and regulatory reporting',
        metrics: 'Enterprise compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat intelligence, anomaly detection, and incident response automation',
        metrics: '24/7 threat protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime with auto-scaling security systems and distributed denial-of-service protection',
        metrics: 'Maximum availability',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Wifi,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layered security with intelligent threat filtering and adaptive defence mechanisms',
        metrics: 'Proactive threat defence',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication',
        metrics: 'Zero-trust security',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Search,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with correlation analysis, threat hunting, and automated incident response',
        metrics: 'Intelligent security ops',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Advanced data protection with encrypted backups, point-in-time recovery, and data loss prevention',
        metrics: 'Complete data protection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and lateral movement prevention',
        metrics: 'Network security isolation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and security posture management',
        metrics: 'Continuous security validation',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'spa-wellness-management': {
    keyBenefits: [
      {
        icon: Heart,
        title: 'Wellness Journey Intelligence',
        description: 'Personalised wellness programmes with treatment tracking, progress monitoring, and outcome optimisation',
        metrics: '60% wellness improvement',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Calendar,
        title: 'Advanced Appointment Management',
        description: 'Intelligent booking system with resource optimisation, therapist matching, and client preference tracking',
        metrics: '45% booking efficiency increase',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Treatment Analytics & Optimisation',
        description: 'Advanced treatment analysis with effectiveness tracking, client satisfaction, and service improvement',
        metrics: '40% treatment success rate',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Users,
        title: 'Member Retention Excellence',
        description: 'Comprehensive membership management with loyalty programmes, retention strategies, and lifetime value tracking',
        metrics: '70% member retention increase',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Target,
        title: 'Revenue Optimisation Engine',
        description: 'Dynamic pricing strategies with package optimisation, upsell automation, and profitability analysis',
        metrics: '35% revenue per client increase',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Wellness Performance Analytics',
        description: 'Comprehensive wellness metrics with client progress tracking, treatment outcomes, and business intelligence',
        metrics: 'Complete wellness intelligence',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Client Wellness Profiles',
        description: 'Advanced client management with health history, treatment preferences, and wellness goal tracking',
        metrics: 'Complete client intelligence',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Cpu,
        title: 'AI Treatment Recommendations',
        description: 'Machine learning-powered treatment suggestions with personalisation, outcome prediction, and wellness optimisation',
        metrics: '95% treatment matching accuracy',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Settings,
        title: 'Therapist Management Platform',
        description: 'Comprehensive staff management with scheduling optimisation, performance tracking, and certification management',
        metrics: 'Therapist excellence platform',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Package,
        title: 'Inventory & Product Management',
        description: 'Advanced inventory control with product tracking, automated ordering, and retail sales integration',
        metrics: 'Inventory optimisation mastery',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Heart,
        title: 'Wellness Programme Builder',
        description: 'Customisable wellness programmes with progress tracking, goal setting, and achievement recognition',
        metrics: 'Personalised wellness journeys',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Globe,
        title: 'Marketing Automation Suite',
        description: 'Targeted wellness marketing with client segmentation, campaign automation, and retention strategies',
        metrics: '85% marketing effectiveness',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated audit trails and regulatory reporting',
        metrics: 'Enterprise compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat intelligence, anomaly detection, and incident response automation',
        metrics: '24/7 threat protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime with auto-scaling security systems and distributed denial-of-service protection',
        metrics: 'Maximum availability',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Wifi,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layered security with intelligent threat filtering and adaptive defence mechanisms',
        metrics: 'Proactive threat defence',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication',
        metrics: 'Zero-trust security',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Search,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with correlation analysis, threat hunting, and automated incident response',
        metrics: 'Intelligent security ops',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Advanced data protection with encrypted backups, point-in-time recovery, and data loss prevention',
        metrics: 'Complete data protection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and lateral movement prevention',
        metrics: 'Network security isolation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and security posture management',
        metrics: 'Continuous security validation',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'event-management-platform': {
    keyBenefits: [
      {
        icon: Calendar,
        title: 'Complete Event Planning Suite',
        description: 'Comprehensive event management with timeline planning, task automation, and collaborative project management',
        metrics: '50% planning efficiency increase',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Guest Experience Excellence',
        description: 'Advanced guest management with registration, check-in automation, and personalised event experiences',
        metrics: '65% guest satisfaction increase',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Target,
        title: 'Vendor Coordination Intelligence',
        description: 'Streamlined vendor management with performance tracking, contract management, and quality assurance',
        metrics: '40% vendor efficiency improvement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: BarChart3,
        title: 'Budget Management Excellence',
        description: 'Advanced financial tracking with cost optimisation, ROI analysis, and profitability management',
        metrics: '30% budget optimisation',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Marketing & Promotion Engine',
        description: 'Integrated marketing automation with social media management, campaign tracking, and attendee acquisition',
        metrics: '45% attendee increase',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Event Analytics & Insights',
        description: 'Comprehensive event analysis with attendee behaviour, engagement metrics, and success measurement',
        metrics: 'Complete event intelligence',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Venue & Resource Management',
        description: 'Advanced venue optimisation with space allocation, resource scheduling, and capacity management',
        metrics: 'Venue utilisation mastery',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Cpu,
        title: 'AI Event Optimisation',
        description: 'Machine learning-powered event planning with attendance prediction, preference analysis, and success forecasting',
        metrics: '96% event success prediction',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Heart,
        title: 'Communication & Collaboration Hub',
        description: 'Integrated communication platform with team collaboration, client updates, and stakeholder management',
        metrics: '90% communication efficiency',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Settings,
        title: 'Workflow Automation Engine',
        description: 'Intelligent process automation with task scheduling, approval workflows, and deadline management',
        metrics: '80% workflow automation',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Package,
        title: 'Catering & Service Management',
        description: 'Comprehensive catering coordination with menu planning, dietary management, and service scheduling',
        metrics: 'Catering excellence platform',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: MapPin,
        title: 'Multi-Location Event Management',
        description: 'Advanced multi-venue coordination with logistics management, transportation, and synchronised planning',
        metrics: 'Complex event mastery',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated audit trails and regulatory reporting',
        metrics: 'Enterprise compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat intelligence, anomaly detection, and incident response automation',
        metrics: '24/7 threat protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime with auto-scaling security systems and distributed denial-of-service protection',
        metrics: 'Maximum availability',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Wifi,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layered security with intelligent threat filtering and adaptive defence mechanisms',
        metrics: 'Proactive threat defence',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication',
        metrics: 'Zero-trust security',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Search,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with correlation analysis, threat hunting, and automated incident response',
        metrics: 'Intelligent security ops',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Advanced data protection with encrypted backups, point-in-time recovery, and data loss prevention',
        metrics: 'Complete data protection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and lateral movement prevention',
        metrics: 'Network security isolation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and security posture management',
        metrics: 'Continuous security validation',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'fitness-gym-management': {
    keyBenefits: [
      {
        icon: Award,
        title: 'Membership Intelligence Platform',
        description: 'Advanced membership management with automated billing, retention analytics, and lifetime value optimisation',
        metrics: '50% membership retention increase',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Calendar,
        title: 'Class & Training Optimisation',
        description: 'Intelligent class scheduling with capacity management, trainer assignment, and member preference tracking',
        metrics: '45% class utilisation improvement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: TrendingUp,
        title: 'Fitness Progress Analytics',
        description: 'Comprehensive fitness tracking with progress monitoring, goal setting, and personalised workout recommendations',
        metrics: '60% member achievement rate',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Personal Training Excellence',
        description: 'Advanced trainer management with client matching, session tracking, and performance optimisation',
        metrics: '70% training satisfaction',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Target,
        title: 'Equipment Management Intelligence',
        description: 'Smart equipment tracking with maintenance scheduling, utilisation analytics, and safety monitoring',
        metrics: '40% equipment efficiency increase',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: BarChart3,
        title: 'Revenue Performance Analytics',
        description: 'Comprehensive revenue analysis with profitability tracking, pricing optimisation, and growth forecasting',
        metrics: '35% revenue per member increase',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Member Health Profile System',
        description: 'Advanced member database with health history, fitness goals, and progress tracking capabilities',
        metrics: 'Complete member intelligence',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Cpu,
        title: 'AI Workout Personalisation',
        description: 'Machine learning-powered workout recommendations with adaptation based on progress and preferences',
        metrics: '95% workout satisfaction',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Heart,
        title: 'Wellness Programme Management',
        description: 'Comprehensive wellness initiatives with nutrition tracking, lifestyle coaching, and holistic health management',
        metrics: 'Complete wellness ecosystem',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Settings,
        title: 'Staff Performance Platform',
        description: 'Advanced staff management with performance metrics, training tracking, and certification management',
        metrics: 'Staff excellence optimisation',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'Access Control & Security',
        description: 'Intelligent facility access with member check-in automation, security monitoring, and safety compliance',
        metrics: '99% facility security',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Globe,
        title: 'Community Engagement Platform',
        description: 'Social fitness features with challenges, leaderboards, and community building tools',
        metrics: '85% member engagement',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated audit trails and regulatory reporting',
        metrics: 'Enterprise compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat intelligence, anomaly detection, and incident response automation',
        metrics: '24/7 threat protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime with auto-scaling security systems and distributed denial-of-service protection',
        metrics: 'Maximum availability',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Wifi,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layered security with intelligent threat filtering and adaptive defence mechanisms',
        metrics: 'Proactive threat defence',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication',
        metrics: 'Zero-trust security',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Search,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with correlation analysis, threat hunting, and automated incident response',
        metrics: 'Intelligent security ops',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Advanced data protection with encrypted backups, point-in-time recovery, and data loss prevention',
        metrics: 'Complete data protection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and lateral movement prevention',
        metrics: 'Network security isolation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and security posture management',
        metrics: 'Continuous security validation',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'customer-service-platform': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Multi-Channel Support Excellence',
        description: 'Unified customer support across all channels with intelligent routing, priority management, and response automation',
        metrics: '50% response time reduction',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Customer Satisfaction Analytics',
        description: 'Advanced satisfaction tracking with sentiment analysis, feedback collection, and satisfaction improvement strategies',
        metrics: '65% satisfaction increase',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Target,
        title: 'Agent Performance Optimisation',
        description: 'Comprehensive agent management with performance tracking, training identification, and productivity enhancement',
        metrics: '40% agent productivity increase',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Clock,
        title: 'Live Chat & Real-Time Support',
        description: 'Advanced live chat system with intelligent routing, chatbot integration, and real-time customer assistance',
        metrics: '60% instant resolution rate',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Database,
        title: 'Knowledge Base Intelligence',
        description: 'AI-powered knowledge management with self-service optimisation, content recommendations, and usage analytics',
        metrics: '70% self-service resolution',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Support Analytics & Insights',
        description: 'Comprehensive support metrics with trend analysis, performance forecasting, and improvement recommendations',
        metrics: 'Complete support intelligence',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: 'AI-Powered Ticket Routing',
        description: 'Machine learning-driven ticket classification with intelligent agent assignment and priority optimisation',
        metrics: '95% routing accuracy',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Settings,
        title: 'Workflow Automation Engine',
        description: 'Advanced automation with rule-based routing, escalation management, and response template optimisation',
        metrics: '80% workflow automation',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Globe,
        title: 'CRM Integration Platform',
        description: 'Seamless CRM integration with customer history, interaction tracking, and unified customer profiles',
        metrics: 'Complete customer context',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Heart,
        title: 'Customer Journey Intelligence',
        description: 'Advanced journey mapping with touchpoint analysis, experience optimisation, and proactive support',
        metrics: 'Customer journey mastery',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Package,
        title: 'Quality Assurance System',
        description: 'Comprehensive quality management with interaction monitoring, coaching recommendations, and service improvement',
        metrics: '99% quality standards',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Star,
        title: 'Predictive Support Analytics',
        description: 'AI-powered support prediction with issue forecasting, resource planning, and proactive customer outreach',
        metrics: 'Proactive support excellence',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated audit trails and regulatory reporting',
        metrics: 'Enterprise compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat intelligence, anomaly detection, and incident response automation',
        metrics: '24/7 threat protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime with auto-scaling security systems and distributed denial-of-service protection',
        metrics: 'Maximum availability',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Wifi,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layered security with intelligent threat filtering and adaptive defence mechanisms',
        metrics: 'Proactive threat defence',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication',
        metrics: 'Zero-trust security',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Search,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with correlation analysis, threat hunting, and automated incident response',
        metrics: 'Intelligent security ops',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Advanced data protection with encrypted backups, point-in-time recovery, and data loss prevention',
        metrics: 'Complete data protection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and lateral movement prevention',
        metrics: 'Network security isolation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and security posture management',
        metrics: 'Continuous security validation',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  }
};